import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, Navigation, Map, Building2, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import LocationModal from "./LocationModal";
import { translateCityName, translateLocationString } from "../../lib/cityTranslations";
import useVisitorCity from "../../hooks/useVisitorCity";

export default function LocationDropdown({ selectedLocation, isOpen, onToggle, onSelect, hideTrigger = false }) {
  const { t, i18n } = useTranslation('location');
  const lang = i18n.language;
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState({});
  const [expandedCity, setExpandedCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const visitorCity = useVisitorCity();

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_REACT_APP_API_URL}/api/cities/cities`);
        const data = await response.json();
        if (data.success) setCities(data.data);
      } catch (err) {
        console.error("Failed to fetch cities:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCities();
  }, []);

  useEffect(() => {
    if (!isOpen) setExpandedCity(null);
  }, [isOpen]);

  const getCityLabel = (city) => {
    if (lang === 'en' && city.name_en) return city.name_en;
    if (lang === 'ru' && city.name_ru) return city.name_ru;
    return city.name;
  };

  const fetchDistrictsForCity = async (cityName) => {
    if (districts[cityName]) return;
    try {
      const response = await fetch(`${import.meta.env.VITE_REACT_APP_API_URL}/api/cities/districts?city=${encodeURIComponent(cityName)}`);
      const data = await response.json();
      if (data.success && data.data.length > 0) {
        setDistricts((prev) => ({ ...prev, [cityName]: data.data }));
      }
    } catch (err) {
      console.error("Failed to fetch districts:", err);
    }
  };

  const toggleCityExpansion = (cityName) => {
    if (expandedCity === cityName) setExpandedCity(null);
    else { setExpandedCity(cityName); fetchDistrictsForCity(cityName); }
  };

  // Always pass BG canonical name (city.name) to onSelect — backend queries use it
  const handleSelectCity = (cityName) => { onSelect(cityName); setExpandedCity(null); };
  const handleSelectDistrict = (cityName, district) => { onSelect(`${cityName} - ${district}`); setExpandedCity(null); };
  const handleLocationModalSelect = (locationData) => {
    onSelect({ type: "location", data: locationData });
    setShowLocationModal(false);
  };

  const getDisplayText = () => {
      if (!selectedLocation) return loading ? t('loading') : translateCityName(visitorCity || 'София', lang);
    if (typeof selectedLocation === "object" && selectedLocation.type === "location") {
      return selectedLocation.data.address?.split(",")[0] || t('around_me');
    }
    // selectedLocation is a BG canonical city name, optionally "City - District" —
    // prefer the fetched list (matches backend name_en/name_ru exactly), fall back
    // to the static map so translation still works before the list has loaded.
    const cityObj = cities.find(c => c.name === selectedLocation);
    if (cityObj) return getCityLabel(cityObj);
    return translateLocationString(selectedLocation, lang);
  };

  /* Shared dropdown list — rendered either absolutely (when hideTrigger) or
     inline below the trigger (original behaviour) */
  const DropdownList = () => (
    <div
      className="absolute top-full left-0 mt-2 w-80 rounded-2xl max-h-80 overflow-y-auto z-50"
      style={{
        background: "rgba(255,255,255,0.85)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        boxShadow: "0 16px 48px rgba(100,100,160,0.14)",
        border: "1px solid rgba(255,255,255,0.90)",
      }}
    >
      <ul className="py-1">
        {/* Около мен */}
        <li
          className="px-4 py-3 cursor-pointer flex items-center gap-3 transition-colors"
          style={{ background: "transparent", borderBottom: "1px solid rgba(108,142,239,0.10)" }}
          onClick={() => { setShowLocationModal(true); onToggle(); }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(108,142,239,0.06)"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(108,142,239,0.12)" }}>
            <Navigation className="w-4 h-4" style={{ color: "#6C8EEF" }} />
          </div>
          <span style={{ fontSize: "13px", fontWeight: 500, color: "#2D2D4E" }}>{t('around_me')}</span>
        </li>

        {/* Избери от картата */}
        <li
          className="px-4 py-3 cursor-pointer flex items-center gap-3 transition-colors"
          style={{ background: "transparent", borderBottom: "1px solid rgba(108,142,239,0.10)" }}
          onClick={() => { setShowLocationModal(true); onToggle(); }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(139,127,199,0.06)"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(139,127,199,0.12)" }}>
            <Map className="w-4 h-4" style={{ color: "#8B7FC7" }} />
          </div>
          <span style={{ fontSize: "13px", fontWeight: 500, color: "#2D2D4E" }}>{t('pick_from_map')}</span>
        </li>

        {loading ? (
          <li className="px-4 py-3 text-center" style={{ fontSize: "13px", color: "#8A8AAA" }}>{t('loading')}</li>
        ) : cities.length > 0 ? (
          cities.map((city) => (
            <li key={city.name} style={{ borderBottom: "1px solid rgba(108,142,239,0.08)" }} className="last:border-b-0">
              <div className="flex items-center justify-between">
                <div
                  className="flex-1 px-4 py-3 cursor-pointer flex items-center gap-3 transition-colors"
                  style={{ background: "transparent" }}
                  onClick={() => handleSelectCity(city.name)}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(108,142,239,0.05)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(232,236,247,0.80)" }}>
                    <Building2 className="w-4 h-4" style={{ color: "#5A5A7A" }} />
                  </div>
                  <span style={{ fontSize: "13px", fontWeight: 500, color: "#2D2D4E" }}>{getCityLabel(city)}</span>
                </div>
                <button
                  onClick={e => { e.stopPropagation(); toggleCityExpansion(city.name); }}
                  className="px-4 py-3 rounded-r-2xl transition-colors"
                  style={{ background: "transparent" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(108,142,239,0.08)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  {expandedCity === city.name
                    ? <ChevronDown className="w-4 h-4" style={{ color: "#6C8EEF" }} />
                    : <ChevronRight className="w-4 h-4" style={{ color: "#8A8AAA" }} />
                  }
                </button>
              </div>

              {expandedCity === city.name && (
                <div style={{ background: "rgba(108,142,239,0.04)", borderTop: "1px solid rgba(108,142,239,0.10)" }}>
                  {districts[city.name] ? (
                    districts[city.name].length > 0 ? (
                      districts[city.name].map((district) => (
                        <div
                          key={district}
                          className="pl-14 pr-4 py-2.5 cursor-pointer flex items-center gap-2 transition-colors"
                          style={{ background: "transparent" }}
                          onClick={() => handleSelectDistrict(city.name, district)}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(108,142,239,0.08)"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#6C8EEF" }} />
                          <span style={{ fontSize: "13px", color: "#5A5A7A" }}>{district}</span>
                        </div>
                      ))
                    ) : (
                      <div className="pl-14 pr-4 py-2.5" style={{ fontSize: "13px", color: "#8A8AAA" }}>{t('no_districts')}</div>
                    )
                  ) : (
                    <div className="pl-14 pr-4 py-2.5" style={{ fontSize: "13px", color: "#8A8AAA" }}>{t('loading')}</div>
                  )}
                </div>
              )}
            </li>
          ))
        ) : (
          <li className="px-4 py-3 text-center" style={{ fontSize: "13px", color: "#8A8AAA" }}>{t('no_cities')}</li>
        )}
      </ul>
    </div>
  );

  return (
    <>
      <div className="w-full">
        {/* ── Mode A: hideTrigger — parent owns the trigger UI, we just render
            the dropdown list when isOpen. Used by SearchResults nav card. ── */}
        {hideTrigger ? (
          <>
            {isOpen && (
              <div style={{ position: "relative" }}>
                <DropdownList />
              </div>
            )}
          </>
        ) : (
          /* ── Mode B: original self-contained trigger + dropdown ─────── */
          <>
            {/* Trigger */}
            <div className="flex items-center gap-3 py-3 cursor-pointer" onClick={onToggle}>
              <div className="flex-1 min-w-0">
                <p style={{ fontSize: "10px", fontWeight: 700, color: "#8A8AAA", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: "2px" }}>
                  {t('label')}
                </p>
                <p style={{ fontSize: "14px", fontWeight: 600, color: "#2D2D4E" }} className="truncate">
                  {getDisplayText()}
                </p>
              </div>
              <ChevronDown
                className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                style={{ color: "#8A8AAA" }}
              />
            </div>

            {/* Dropdown */}
            {isOpen && <DropdownList />}
          </>
        )}
      </div>

      <LocationModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onSelectLocation={handleLocationModalSelect}
      />
    </>
  );
}